import { matchRoutes, type createBrowserRouter } from 'react-router';
import type { QueryClient } from '@tanstack/react-query';
import type { ShopNavigation } from '@/contexts/ShopContext';
import { dashboardV3NavItems, type NavChild, type NavItem } from '@/lib/navigation';
import { ownerPath, stripLegacyOwnerPrefix } from '@/lib/hosts';
import { api, ApiError } from '@/lib/api';
import type { ShopDetail } from '@/hooks/queries/useCurrentShopDetail';

// Module prefixes also cover deep links absent from the sidebar (bar dashboard,
// variance, investor, etc.). Navigation visibility alone is not a route policy.
export const SHOP_MODULE_ROUTES = {
  '/commerce': 'commerce_pages_enabled',
  '/rental': 'rental_mode_enabled',
  '/bar': 'bar_mode_enabled',
  '/partners': 'partner_mode_enabled',
  '/returns': 'returns_enabled',
  '/drivers': 'drivers_enabled',
  '/deliveries': 'drivers_enabled',
  '/products/ingredients': 'ingredient_management_enabled',
  '/payroll': 'payroll_enabled',
} as const;

export function isShopRouteAllowed(
  pathname: string,
  detail: ShopDetail,
  grants: string[],
  accountingPermissions: readonly string[] = [],
): boolean {
  const path = stripLegacyOwnerPrefix(pathname);
  if (path === '/' || path === '/dashboard') return true;
  for (const [prefix, flag] of Object.entries(SHOP_MODULE_ROUTES)) {
    if ((path === prefix || path.startsWith(`${prefix}/`)) && !detail[flag]) return false;
  }
  if (path === '/pos')
    return grants.some((grant) => ['*', 'sales.*', 'sales.create'].includes(grant));
  const entries = dashboardV3NavItems.flatMap((parent) => [
    ...(parent.children ?? []).map((entry) => ({ entry, parent })),
    { entry: parent, parent },
  ]);
  const match = entries
    .filter(({ entry }) => path === entry.href || path.startsWith(`${entry.href}/`))
    .sort((a, b) => b.entry.href.length - a.entry.href.length)[0];
  if (!match) return true;
  const allowed = (entry: NavItem | NavChild) => {
    if (entry.featureFlag && !detail[entry.featureFlag]) return false;
    const permission = entry.permission;
    if (!permission) return true;
    if (permission === 'finances.access')
      return (
        accountingPermissions.includes('read') ||
        grants.some((grant) => ['*', 'reports.*', 'reports.view'].includes(grant))
      );
    if (permission.startsWith('accounting.')) {
      return accountingPermissions.includes(permission.split('.')[1] ?? '');
    }
    return grants.some(
      (grant) => grant === '*' || grant === permission || grant === `${permission.split('.')[0]}.*`,
    );
  };
  if (path === '/finances') return allowed(match.parent);
  return allowed(match.parent) && allowed(match.entry);
}

/** Resolve access while the old page is unmounted, before mounting new queries. */
export function createShopNavigation(
  router: ReturnType<typeof createBrowserRouter>,
  client: QueryClient,
): ShopNavigation {
  return {
    canLeave: () =>
      [...router.state.blockers.values()].every((blocker) => blocker.state === 'unblocked'),
    destination: () => {
      const matches = matchRoutes(router.routes, router.state.location) ?? [];
      const handle = matches[matches.length - 1]?.route.handle as
        | { shopSwitchTo?: string }
        | undefined;
      return handle?.shopSwitchTo ? ownerPath(handle.shopSwitchTo) : router.state.location.pathname;
    },
    prepare: async (path) => {
      // Ask existing router blockers before their forms are unmounted. A reset
      // cancels the switch, while proceed lets it continue after navigation.
      const previousKey = router.state.location.key;
      await router.navigate(path, { replace: true });
      if (![...router.state.blockers.values()].some((blocker) => blocker.state !== 'unblocked'))
        return true;
      return new Promise<boolean>((resolve) => {
        const unsubscribe = router.subscribe((state) => {
          if (state.navigation.state !== 'idle') return;
          const blockers = [...state.blockers.values()];
          if (blockers.some((blocker) => blocker.state !== 'unblocked')) return;
          unsubscribe();
          resolve(state.location.key !== previousKey);
        });
      });
    },
    navigate: async (path) => {
      await router.navigate(path, { replace: true });
    },
    resolve: async (path, shopId, signal) => {
      const user = client.getQueryData<{ id: string }>(['auth', 'user']);
      const [detail, effective] = await Promise.all([
        client.fetchQuery({
          queryKey: ['shop-detail', shopId],
          queryFn: () => api.get<ShopDetail>(`/shops/${shopId}`, { signal }),
          staleTime: 60_000,
          retry: false,
        }),
        client.fetchQuery({
          queryKey: ['permissions', 'me', shopId, user?.id],
          queryFn: async () =>
            (await api.get<{ role: string; permissions: string[] }>('/permissions/me', { signal }))
              .data,
          retry: false,
        }),
      ]);
      let accountingPermissions: string[] = [];
      if (stripLegacyOwnerPrefix(path).startsWith('/finances')) {
        try {
          const access = await client.fetchQuery({
            queryKey: ['accounting', shopId, 'access'],
            queryFn: async () =>
              (await api.get<{ permissions: string[] }>('/accounting/status', { signal })).data,
            retry: false,
          });
          accountingPermissions = access.permissions;
        } catch (error) {
          if (!(error instanceof ApiError) || error.status !== 403) throw error;
          return ownerPath('/dashboard');
        }
      }
      return isShopRouteAllowed(path, detail.data, effective.permissions, accountingPermissions)
        ? path
        : ownerPath('/dashboard');
    },
  };
}
